import { TreeNode } from 'primeng/api';

export type JsonFieldTypeEnum =
  | 'String'
  | 'Boolean'
  | 'Numeric'
  | 'Date'
  | 'Object'
  | 'Array'
  | 'StringArray'
  | 'BooleanArray'
  | 'NumericArray';

export interface ParseJsonField {
  id?: number;
  name?: string | null;
  type?: JsonFieldTypeEnum;
  childs?: ParseJsonField[] | null;
  /**
   * True when this field is a key produced by a parsing rule rather than a property the payload
   * actually contains. `name` is the extraction key. Such a field is only resolvable for
   * pre-population, so it must not be offered by any other selection surface.
   */
  isParseRuleKey?: boolean;
  /** Parsing-rule field delimiter. Set only on a property that has a rule, never on a key. */
  delimiter?: string | null;
  /** Parsing-rule key/value separator. Set only on a property that has a rule, never on a key. */
  keySeparator?: string | null;
}

export interface ParseJsonResponse {
  fields?: ParseJsonField[] | null;
}

/**
 * Removes parsing-rule keys from a field tree.
 *
 * The input tree is cleaned rather than the output of `mapToTreeNode`, because that function also
 * copies `field.childs` onto `data.children` verbatim - filtering only the mapped children would
 * leave the keys visible to anything reading `data.children`. A side effect worth having: a String
 * property whose only children were keys becomes a leaf again, which is what consumers with a
 * leaf-only rule (the rich-text `{{json.*}}` inserter) expect.
 */
export function stripParseRuleKeys(fields: ParseJsonField[]): ParseJsonField[] {
  return fields
    .filter(field => !field.isParseRuleKey)
    .map(field => {
      if (!field.childs?.length) return field;
      const childs = stripParseRuleKeys(field.childs);
      return { ...field, childs: childs.length ? childs : null };
    });
}

export function mapToTreeNode(
  fields: ParseJsonField[],
  parentKey = '',
  parentPath = ''
): TreeNode[] {
  return fields.map((field, index) => {
    const key = parentKey ? `${parentKey}-${index}` : `${index}`;
    // A parsing-rule key has no path of its own - the delimited string it is extracted from lives at
    // the parent's path, which is what the backend resolves for it too.
    const path = field.isParseRuleKey
      ? parentPath
      : parentPath
        ? `${parentPath}.${field.name ?? ''}`
        : (field.name ?? '');

    const treeNode: TreeNode = {
      key,
      label: field.name ?? '',
      data: {
        type: field.type,
        id: field.id,
        path,
        children: field.childs,
        isParseRuleKey: field.isParseRuleKey === true,
        // This property carries a parsing rule: the wizard offers Parse/Clear on it, and other
        // surfaces show a read-only indicator so a mapper can see why it has children.
        hasParseRule: !!field.delimiter,
        // Carried so the Parse panel can seed itself from the saved rule.
        delimiter: field.delimiter ?? null,
        keySeparator: field.keySeparator ?? null,
      },
    };

    // If the node has children, recursively map them to TreeNodes
    if (field.childs && field.childs.length > 0) {
      treeNode.children = mapToTreeNode(field.childs, key, path);
    }

    return treeNode;
  });
}
